import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';

declare const bootstrap: any;

@Directive({
    selector: '[appBsTooltip]',
    standalone: false
})
export class BsTooltipDirective implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input('appBsTooltip') tooltipText: string = '';
  @Input() onlyWhenTruncated: boolean = false;
  @Input() tooltipClass: string = '';
  @Input() tooltipHtml: boolean = false;
  @Input() triggers: string = 'hover';
  @Input() placement: string = 'top';
  private tooltipInstance: any;
  private truncationCheckTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(private el: ElementRef) {}

  ngOnInit(): void {
    if (!this.onlyWhenTruncated) {
      this.initTooltip();
    }
  }

  ngAfterViewInit(): void {
    if (!this.onlyWhenTruncated) {
      return;
    }
    this.scheduleTruncationCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['tooltipText'] || changes['tooltipText'].firstChange) {
      return;
    }
    if (this.onlyWhenTruncated) {
      if (this.tooltipInstance) {
        this.tooltipInstance.dispose();
        this.tooltipInstance = null;
      }
      this.scheduleTruncationCheck();
    } else if (this.tooltipInstance) {
      this.updateTooltipTitle(this.tooltipText);
    }
  }

  ngOnDestroy(): void {
    this.clearTruncationCheck();
    if (this.tooltipInstance) {
      this.tooltipInstance.dispose();
    }
  }

  private updateTooltipTitle(title: string): void {
    this.tooltipInstance.setContent({ '.tooltip-inner': title });
  }

  private scheduleTruncationCheck(): void {
    this.clearTruncationCheck();
    // Defer to the next event loop tick so the browser has completed layout
    // and scrollWidth/clientWidth reflect the element's actual rendered dimensions.
    this.truncationCheckTimeoutId = setTimeout(() => {
      this.truncationCheckTimeoutId = null;
      this.applyTruncationTooltipIfNeeded();
    });
  }

  private applyTruncationTooltipIfNeeded(): void {
    const el = this.el.nativeElement;
    if (!el.isConnected) {
      return;
    }
    const wraps = getComputedStyle(el).whiteSpace !== 'nowrap';
    const truncated = wraps
      ? el.scrollHeight > el.clientHeight + 1
      : el.scrollWidth > el.clientWidth + 1;
    if (this.tooltipText && truncated) {
      this.initTooltip();
    }
  }

  private clearTruncationCheck(): void {
    if (this.truncationCheckTimeoutId !== null) {
      clearTimeout(this.truncationCheckTimeoutId);
      this.truncationCheckTimeoutId = null;
    }
  }

  private initTooltip(): void {
    if (!this.tooltipText || this.tooltipInstance) {
      return;
    }
    this.tooltipInstance = new bootstrap.Tooltip(this.el.nativeElement, {
      title: this.tooltipText,
      placement: this.placement,
      trigger: this.triggers,
      container: 'body',
      customClass: this.tooltipClass || '',
      html: this.tooltipHtml,
    });
  }
}
